import { useEffect, useRef, useState } from 'react'
import clsx from 'clsx'

import Contact from './components/Contact'
import BasicInfo from './components/BasicInfo'
import Supervising from './components/Supervising'
import BusinessIdea from './components/BusinessIdea'
import BusinessActivity from './components/BusinessActivity'
import RiskAnalysis, { RISK_TABS, RISK_TAB_KEYS } from './components/RiskAnalysis'

import MiniHeader from '../../../../UI/MiniHeader'
import Btn from '../../../../UI/Btn'

import styles from './style.module.scss'

import http from '../../../../shared/http'
import { TApi, TRoutes } from '../../../../shared/const'
import useScrollTopOnTabChange from '../../../../shared/useScrollTopOnTabChange'
import { companySupervision } from '../../../../shared/helpers'
import { useTranslation } from 'react-i18next'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'

/*
 * Unternehmensprofil der Schueler:innen (und der Lehrkraft in der
 * Schueler:innen-Ansicht).
 *
 * Aufgebaut wie das Firmenprofil der Administration (views/admin/CompanyReview):
 * ein senkrechtes Untermenue links, der gewaehlte Abschnitt rechts. Bis
 * 08.08.2026 standen die Abschnitte als waagrechte Reiter darueber und brachen
 * je nach Fensterbreite um; der Zustand des eigenen Unternehmens stand
 * nirgends.
 *
 * Die Markt- und Risikoanalyse ist seit demselben Tag kein eigener Menuepunkt
 * mehr, sondern liegt hier — wie in der Administration — als Abschnitt im
 * Profil. Sie beschreibt dasselbe Unternehmen; zwei Menuepunkte dafuer waren
 * eine Trennung ohne Grund.
 */

const PROFILE_TABS = [
	{ key: 'basic', label: 'Basic Information' },
	{ key: 'business', label: 'Business Activity' }
]

// Die drei Teile der Analyse stehen unter einer eigenen Ueberschrift im
// Untermenue: Sie gehoeren zusammen und werden gemeinsam gespeichert.
const RISK_GROUP = { title: 'Market and Risk Analysis', tabs: RISK_TABS }

const tabFromSearch = search => {
	const wanted = new URLSearchParams(search).get('tab')
	return [...PROFILE_TABS.map(tab => tab.key), ...RISK_TAB_KEYS].includes(wanted) ? wanted : PROFILE_TABS[0].key
}

/**
 * In welchem Zustand ist das eigene Unternehmen? Dieselbe Reihenfolge wie im
 * Firmenprofil der Administration: Eine Ablehnung ueberschreibt alles Weitere,
 * danach zaehlt die Aktivierung, dann die eingereichte Pruefung.
 */
const companyStatus = profile => {
	if (!profile) return null
	if (profile.isRejected) return { key: 'rejected', label: 'Company Rejected', tone: 'Red' }
	if (profile.isActive) return { key: 'active', label: 'Company status active', tone: 'Green' }
	if (profile.isNeedReview) return { key: 'review', label: 'Company status in review', tone: 'Amber' }
	return { key: 'draft', label: 'Company status not activated', tone: 'Grey' }
}

function CompanyProfile(props) {
	const { t } = useTranslation()
	const [profile, setProfile] = useState({})
	const [loading, setLoading] = useState(true)
	const [loadFailed, setLoadFailed] = useState(false)
	// Ampeln der drei Analyse-Teile. Der Abschnitt meldet sie, weil nur er den
	// gespeicherten Stand kennt.
	const [riskStatus, setRiskStatus] = useState({})

	const history = useHistory()
	const { id } = useParams()
	const location = useLocation()
	const path = location.pathname

	const [activeTab, setActiveTab] = useState(() => tabFromSearch(location.search))

	const tabsRef = useRef(null)
	useScrollTopOnTabChange(activeTab, tabsRef)

	/*
	 * Die eigene Company kennt nur der Weg ueber /company. Auf /company/update/:id
	 * schaut die Administration in ein fremdes Profil — die Analyse haengt dort
	 * an der Schnittstelle der Schueler:innen und gehoert deshalb nicht dazu.
	 */
	const showRisk = path === TRoutes.COMPANY

	// Der Abschnitt in der Adresszeile: Ein Verweis aus der Checkliste
	// (`?tab=market`) landet damit im richtigen Abschnitt, und ein Neuladen
	// bleibt dort stehen.
	useEffect(() => {
		setActiveTab(current => {
			const wanted = tabFromSearch(location.search)
			return wanted === current ? current : wanted
		})
	}, [location.search])

	const openTab = key => {
		setActiveTab(key)
		history.replace({ pathname: path, search: `?tab=${key}` })
	}

	// Erst beim Aufruf abfragen: Stand die Abfrage schon im Rendern, kam beim
	// erneuten Laden die alte, bereits beantwortete Anfrage zurueck.
	const requestCompany = () =>
		path === TRoutes.COMPANY ? http.get(TApi.STUDENT_GET_COMPANY) : http.get(TApi.ADMIN_GET_COMPANY_PROFILE(id))

	/*
	 * Scheitert die Abfrage (Netz weg, Server kurz nicht erreichbar), zeigt die
	 * Seite nicht die leeren Formulare: Das sah aus wie ein Profil ohne Inhalt —
	 * bei der Betreuungslehrer:in stand sogar „undefined undefined“ — und ein
	 * Speichern haette die echten Daten ueberschrieben.
	 */
	const fetchCompany = () => {
		setLoading(true)
		requestCompany()
			.then(({ data }) => {
				setProfile(data)
				setLoadFailed(false)
			})
			.catch(() => {
				setLoadFailed(true)
			})
			.finally(() => {
				setLoading(false)
			})
	}

	/*
	 * Still nachladen: Nach dem Speichern eines Abschnitts soll die Ampel am
	 * Abschnitt stimmen. Ueber `fetchCompany` waere die Seite dabei kurz leer und
	 * saemtliche Abschnitte wuerden neu aufgebaut — ungespeicherte Eingaben in
	 * den anderen Abschnitten waeren weg.
	 */
	// Scheitert das stille Nachladen, bleibt der zuletzt geladene Stand stehen;
	// die Meldung dazu kommt aus `shared/http`.
	const refreshProfile = () =>
		requestCompany()
			.then(({ data }) => setProfile(data))
			.catch(() => {})

	useEffect(() => {
		fetchCompany()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	if (loading) {
		//TODO LOADER
		return <div />
	}

	if (loadFailed) {
		return (
			<div className={styles.wrapper}>
				<MiniHeader title={t('companyProfile')} bg="#f8faff" />
				<div className={styles.main}>
					<div className={styles.loadError}>
						<p>{t('The company profile could not be loaded.')}</p>
						<Btn icon="arrow" iconSize={{ w: 10, h: 10 }} iconColor="#fff" onClick={fetchCompany}>
							{t('Try again')}
						</Btn>
					</div>
				</div>
			</div>
		)
	}

	const supervisingTeacher = companySupervision(profile)?.Teacher

	/*
	 * Ampel am Abschnitt — dieselben Bedingungen, an denen die Administration die
	 * Freigabe festmacht (siehe `activations` in views/admin/CompanyReview).
	 * So sieht man vor dem Einreichen, wo noch etwas offen ist, ohne jeden
	 * Abschnitt einzeln durchzugehen.
	 */
	const tabStatus = {
		basic: !!(
			profile?.name &&
			profile?.companyType &&
			profile?.state &&
			profile?.email &&
			profile?.phone &&
			supervisingTeacher?.id &&
			supervisingTeacher?.School
		),
		business: !!(profile?.branch && profile?.description && profile?.offerTypes && profile?.businessIdea),
		...riskStatus
	}

	const sectionProps = {
		companyProfile: profile,
		// Freigabe zur Überarbeitung durch die Verwaltung (#230), vom Server
		// gerechnet — die Abschnitte entsperren danach Branche und Analyse.
		revisionRelease: profile?.revisionRelease,
		isTeacherStudentView: props.isTeacherStudentView,
		fetchCompany,
		refreshProfile
	}

	const status = companyStatus(profile)

	const navItem = (tab, istTeilDerAnalyse = false) => (
		<button
			key={tab.key}
			type="button"
			aria-current={activeTab === tab.key ? 'true' : undefined}
			className={clsx(
				styles.navItem,
				istTeilDerAnalyse && styles.navItemChild,
				activeTab === tab.key && styles.navItemActive
			)}
			onClick={() => openTab(tab.key)}
		>
			<span
				className={clsx(styles.tabDot, tabStatus[tab.key] ? styles.tabDotDone : styles.tabDotOpen)}
				title={tabStatus[tab.key] ? t('Complete') : t('Incomplete')}
			/>
			{t(tab.label)}
		</button>
	)

	return (
		<div className={styles.wrapper}>
			<MiniHeader
				title={profile?.name ? `${t('companyProfile')} ${profile.name}` : t('companyProfile')}
				bg="#f8faff"
			/>

			<div className={styles.layout}>
				{/*
				 * Bleibt beim Scrollen stehen: In einem langen Abschnitt waere der
				 * Wechsel sonst nur durch Zurueckscrollen erreichbar.
				 */}
				<nav className={styles.nav} ref={tabsRef} aria-label={t('companyProfile')}>
					{/*
					 * Der Zustand des Unternehmens steht ueber den Abschnitten: Ob
					 * eingereicht oder aktiviert, entscheidet, ob sich darunter noch
					 * etwas aendern laesst.
					 */}
					{status && (
						<div className={styles.statusCard}>
							<span className={styles.statusCardLabel}>{t('Status')}</span>
							<span className={clsx(styles.statusPill, styles[`statusPill${status.tone}`])}>
								{t(status.label)}
							</span>
						</div>
					)}

					{PROFILE_TABS.map(tab => navItem(tab))}

					{showRisk && (
						<>
							<span className={styles.navGroupTitle}>{t(RISK_GROUP.title)}</span>
							{RISK_GROUP.tabs.map(tab => navItem(tab, true))}
						</>
					)}
				</nav>

				<div className={styles.content}>
					{/*
					 * Alle Abschnitte bleiben im Seiteninhalt und werden nur
					 * ausgeblendet: Jeder ist ein eigenes Formular mit eigenem
					 * Speichern — wer den Abschnitt wechselt, ohne gespeichert zu
					 * haben, soll seine Eingaben wiederfinden.
					 */}
					<div className={styles.panel}>
						<div className={clsx(styles.tabPanel, activeTab !== 'basic' && styles.tabPanelHidden)}>
							<BasicInfo {...sectionProps} />
							<Contact {...sectionProps} />
							<Supervising {...sectionProps} />
						</div>
						{/*
						 * Die Geschaeftsidee steht seit 08.08.2026 an erster Stelle: Sie geht
						 * woertlich in den Firmenbuchauszug und in die Praesentation im
						 * JAA-Portal, ist also die Angabe mit der groessten Wirkung.
						 */}
						<div className={clsx(styles.tabPanel, activeTab !== 'business' && styles.tabPanelHidden)}>
							<BusinessIdea {...sectionProps} />
							<BusinessActivity {...sectionProps} />
						</div>
						{showRisk && (
							<RiskAnalysis
								activeTab={activeTab}
								onNavigate={openTab}
								onStatusChange={setRiskStatus}
								revisionRelease={profile?.revisionRelease}
							/>
						)}
					</div>
				</div>
			</div>
		</div>
	)
}

CompanyProfile.propTypes = {
	isTeacherStudentView: PropTypes.bool.isRequired
}
const mapStateToProps = state => ({
	isTeacherStudentView: state.user.isTeacherStudentView
})

export default connect(mapStateToProps)(CompanyProfile)
